// Stage 201-P: pure correlations builder.
//
// Reads (a) the most recent Parallax cup slot, (b) the most recent
// MoodInfo on the persona's just-completed message, and produces the
// LaughterCorrelations payload to pack into the engram.
//
// Either or both sub-fields may be null:
//   - cup is null when Parallax is disabled, no slot exists, or the
//     last read failed silently (slot.result.failed === true).
//   - mood is null when no mood-bearing assistant message exists in
//     this turn (e.g. the persona produced text but no mood signal).

import type { CupSlot } from '../../api/parallax';
import type { MoodInfo } from '../../hooks/useChat';
import type { LaughterCorrelations } from './types';

export function buildCorrelations(args: {
  slot: CupSlot | null;
  mood: MoodInfo | null;
}): LaughterCorrelations {
  // A failed Parallax read is treated as "not read" — cup stays null.
  const usableSlot = args.slot && !args.slot.result.failed ? args.slot : null;

  const cup = usableSlot
    ? {
        state: usableSlot.result.cupState,
        splashAnchor: usableSlot.result.splashAnchor,
        splashDirection: usableSlot.result.splashDirection,
        moodLabel: usableSlot.moodLabel,
        cupReadId: usableSlot.result.cupReadId,
        readAt: usableSlot.result.generatedAt,
      }
    : null;

  const mood = args.mood
    ? {
        label: args.mood.label,
        valence: args.mood.valence,
        arousal: args.mood.arousal,
        signalSource: args.mood.signalSource,
        directive: args.mood.directive ?? null,
      }
    : null;

  return { cup, mood };
}
